import React, { useEffect, useState } from "react";
import { UnityCanvas } from "./components/UnityCanvas";
import log from "./util/logger";
import styles from "./App.module.scss";

interface RequestUserTokenJson {
    login_id: string;
    login_type: string;
    password: string;
    token_2fa: string;
}

export interface RequestUserTokenResponseJson {
    access_token: string;
    refresh_token: string;
    expires_in: number;
}

interface LoginResponse {
    success: boolean;
    response: RequestUserTokenResponseJson | null;
    errorCode: string | null;
    errorStatus: string | null;
    errorResponse: ErrorResponse | null;
}

const CLIENT_VERSION = "29.0.0";

const loginURL = "https://prd.xrccg.com:4000/api/v1/user/token/get";

const backgroundStyle: React.CSSProperties = {
    backgroundImage: `url(${process.env.PUBLIC_URL + "/bg_login.jpg"})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
};

interface ErrorResponse {
    errorCode: string;
    message: string;
    requestId: string;
}

const errorMessageTable: Record<string, string> = {
    E0001: "Authentication failed.\nPlease check your credentials and log in again.",
    E0002: "A communication error occurred.\nPlease reload the page from your browser.",
    E0043: "The client in use is not a valid version.\nPlease update to the latest version.",
    E0083: "Login failed or temporarily unavailable.",
    E0089: "This service cannot be used from the current source IP address.",
};

async function login(id: string, type: string, pass: string, code: string): Promise<LoginResponse> {
    const userTokenJson: RequestUserTokenJson = {
        login_id: id,
        login_type: type,
        password: pass,
        token_2fa: code,
    };

    try {
        const response = await fetch(loginURL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Client-Version": CLIENT_VERSION,
            },
            body: JSON.stringify(userTokenJson),
        });
        if (!response.ok) {
            const errorText = await response.text();
            const errorResponse = JSON.parse(errorText);
            console.error("login failed: ", errorText);
            return {
                success: false,
                response: null,
                errorCode: "API_ERROR",
                errorStatus: `HTTP ${response.status}`,
                errorResponse: errorResponse,
            };
        }
        const json: RequestUserTokenResponseJson = await response.json();
        console.log(`login success: access_token = ${json.access_token}, refresh_token = ${json.refresh_token}, expires_in = ${json.expires_in}`);
        return {
            success: true,
            response: json,
            errorCode: null,
            errorStatus: null,
            errorResponse: null,
        };
    } catch (error: any) {
        console.error("login Exception: ", error);
        return {
            success: false,
            response: null,
            errorCode: "API_EXCEPTION",
            errorStatus: error.message,
            errorResponse: null,
        };
    }
}

function App() {
    const [userTokenJson, setUserTokenJson] = useState<RequestUserTokenResponseJson | null>(null);
    const [loginRequired, setLoginRequired] = useState(false);
    const [loginId, setLoginId] = useState("");
    const [password, setPassword] = useState("");
    const [mfaCode, setMfaCode] = useState("");
    const [loginType, setLoginType] = useState<"basic" | "qr_2fa">("basic");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        const url = new URL(window.location.href);
        const id = url.searchParams.get("id");

        if (id == null) {
            setLoginRequired(true);
        } else {
            login(id, "anonymous", "", "").then((result) => {
                if (result.success && result.response !== null) {
                    setUserTokenJson(result.response);
                } else if (result.errorResponse !== null) {
                    const errorMessage = errorMessageTable[result.errorResponse.errorCode];
                    const displayMessage = errorMessage + "\n" + result.errorResponse.errorCode.toString();
                    setErrorMessage(displayMessage);
                }
            });
        }
    }, []);

    const handleManualLogin = async () => {
        const result = await login(loginId, loginType, password, mfaCode);
        if (result.success && result.response) {
            setUserTokenJson(result.response);
        } else if (result.errorResponse !== null) {
            const errorMessage = errorMessageTable[result.errorResponse.errorCode];
            const displayMessage = errorMessage + "\n" + result.errorResponse.errorCode.toString();
            setErrorMessage(displayMessage);
        }
    };

    // トークン取得完了後はUnity画面表示
    if (userTokenJson != null) {
        return <UnityCanvas userTokenJson={userTokenJson} />;
    }

    // ログインフォーム
    if (loginRequired) {
        return (
            <div className={styles.container} style={backgroundStyle}>
                <div className={styles.loginBox}>
                    <h2>NTT XR Concierge</h2>
                    <input
                        className={styles.inputField}
                        type="text"
                        placeholder="User Name"
                        value={loginId}
                        onChange={(e) => setLoginId(e.target.value)}
                        style={{ margin: "8px", padding: "8px", width: "250px" }}
                    />
                    <input
                        className={styles.inputField}
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{ margin: "8px", padding: "8px", width: "250px" }}
                    />
                    <input
                        className={styles.inputField}
                        type="text"
                        placeholder="MFA Code"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        disabled={loginType === "basic"}
                        style={{ margin: "8px", padding: "8px", width: "250px" }}
                    />
                    <div className={styles.radioRow}>
                        <label>
                            <input
                                type="radio"
                                name="loginType"
                                value="basic"
                                checked={loginType === "basic"}
                                onChange={() => setLoginType("basic")}
                            />
                            Basic
                        </label>
                        <label style={{ marginLeft: "1em" }}>
                            <input
                                type="radio"
                                name="loginType"
                                value="qr_2fa"
                                checked={loginType === "qr_2fa"}
                                onChange={() => setLoginType("qr_2fa")}
                            />
                            MFA
                        </label>
                    </div>
                    <button onClick={handleManualLogin} style={{ marginTop: "16px", padding: "10px 20px" }}>
                        Login
                    </button>
                </div>
                {errorMessage && (
                    <div className={styles.errorOverlay}>
                        <div className={styles.errorDialog}>
                            <h3>Network error</h3>
                            <p>{errorMessage}</p>
                            <button onClick={() => setErrorMessage(null)}>Retry</button>
                        </div>
                    </div>
                )}
            </div>
        );
    } else {
        return (
            <div className={styles.container} style={backgroundStyle}>
                {errorMessage && (
                    <div className={styles.errorOverlay}>
                        <div className={styles.errorDialog}>
                            <h3>Network error</h3>
                            <p>{errorMessage}</p>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return <></>;
}

export default App;
